import * as React from 'react';
import { useEffect, useLayoutEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  isRouteErrorResponse,
  useNavigate,
  useRouteError,
} from 'react-router-dom';
import * as Sentry from '@sentry/react';
import {
  Button,
  ButtonContainer,
  ErrorPageContainer,
} from '@seller-hermes/pages/error/error-page.styles';
import SuggestDialog from '@seller-hermes/features/ui/Suggest/suggest.dialog';
import { useStoreActions } from '@seller-hermes/hooks/easy-peasy';
import {
  hasReloadedRecently,
  isChunkLoadError,
  isLeavingPage,
  isReloading,
  tryReloadOnce,
} from '@seller-hermes/utils/chunk-error';

// Se o reload agendado não trocar o documento em até 10s (rede caiu no meio,
// navegação bloqueada), saímos do spinner e mostramos a tela com botão — pra
// não deixar o usuário preso num spinner infinito.
const RELOAD_SAFETY_TIMEOUT = 10_000;

const ErrorPage = () => {
  const { t } = useTranslation('error-page');
  const { open } = useStoreActions((s) => s.dialogs.suggest);
  const navigate = useNavigate();
  // Quando uma rota lazy falha por chunk obsoleto (deploy trocou os arquivos
  // hasheados no Netlify), o React Router captura o erro AQUI — não cai nos
  // handlers globais de `instrument.ts`, que só pegam erros não-capturados.
  // Então é aqui que o force-refresh precisa acontecer. `tryReloadOnce` tem
  // guarda de TTL (60s) contra loop de reload em deploy genuinamente quebrado.
  const error = useRouteError();
  const isChunk = isChunkLoadError(
    error instanceof Error
      ? error
      : String((error as { message?: string } | null)?.message ?? error ?? ''),
  );

  // Timeout de segurança: se o reload não completar, cai pra tela com botão.
  const [reloadTimedOut, setReloadTimedOut] = useState(false);

  // `isReloading()` cobre o caso da Camada B (o listener de vite:preloadError já
  // agendou o reload, inclusive quando o erro que chega aqui é derivado e não
  // casa com isChunkLoadError). `isChunk && !hasReloadedRecently()` cobre o caso
  // de a rota lazy falhar direto no errorElement sem passar pela Camada B.
  //
  // Documento saindo (redirect pro portal, logout): o erro é o import() que a
  // navegação abortou. Não recarrega (cancelaria a saída) nem reporta — só
  // segura o spinner até a página trocar.
  const leaving = isLeavingPage();
  const willAutoReload =
    !reloadTimedOut &&
    !leaving &&
    (isReloading() || (isChunk && !hasReloadedRecently()));
  const waitingNavigation = willAutoReload || (leaving && !reloadTimedOut);

  // useLayoutEffect (não useEffect) pra disparar antes do paint. Não impede o
  // paint — location.reload() só agenda a navegação — mas o que de fato mata o
  // flash é o branch abaixo: no caminho de auto-reload nunca commitamos o
  // ErrorPageContainer, que pinta 100vw/100vh com a cor primária do seller.
  useLayoutEffect(() => {
    if (willAutoReload) tryReloadOnce('react-router-error-element');
  }, [willAutoReload]);

  useEffect(() => {
    if (!waitingNavigation) return;
    const id = setTimeout(() => setReloadTimedOut(true), RELOAD_SAFETY_TIMEOUT);
    return () => clearTimeout(id);
  }, [waitingNavigation]);

  // O errorElement CAPTURA o erro de render/loader: ele não chega no
  // ErrorBoundary do Sentry nem nos handlers globais. Sem isto, tela quebrada
  // em rota só aparecia como breadcrumb de console em outro evento. Chunk e
  // reload em voo já são tratados (e reportados) pela recuperação de chunk;
  // 404 de rota não é erro do app.
  const shouldReport =
    !isChunk &&
    !isReloading() &&
    !leaving &&
    !(isRouteErrorResponse(error) && error.status === 404);
  useEffect(() => {
    if (!shouldReport) return;
    Sentry.withScope((scope) => {
      scope.setTag('mechanism', 'react-router-error-element');
      Sentry.captureException(
        error instanceof Error
          ? error
          : new Error(
              isRouteErrorResponse(error)
                ? `Route error ${error.status} ${error.statusText}`
                : String((error as { message?: string } | null)?.message ?? error),
            ),
      );
    });
  }, [error, shouldReport]);

  // Chunk obsoleto pós-deploy, reload a caminho: mostramos o MESMO spinner do
  // Suspense dos layouts — lê como "carregando", que é o que está ocorrendo.
  if (waitingNavigation) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
      </div>
    );
  }

  // Chunk falhou de novo dentro do TTL (deploy quebrado) ou o reload estourou o
  // timeout: aí sim mostramos a tela e damos uma saída manual.
  if (isChunk) {
    return (
      <ErrorPageContainer>
        <h1>{t('outdated-title')}</h1>
        <p>{t('outdated-message')}</p>
        <ButtonContainer>
          <Button onClick={() => window.location.reload()}>
            {t('reload-now')}
          </Button>
        </ButtonContainer>
      </ErrorPageContainer>
    );
  }

  return (
    <ErrorPageContainer>
      <SuggestDialog />
      <h1>{t('unexpected-error')}</h1>
      <p>{t('message')}</p>
      <ButtonContainer>
        <Button onClick={() => navigate('/orders')}>{t('try-again')}</Button>
        <Button onClick={() => open()}>{t('contact-us')}</Button>
      </ButtonContainer>
    </ErrorPageContainer>
  );
};

export default ErrorPage;
